export const IMAGE_SOURCE_EVIDENCE_SCHEMA_VERSION =
  "cogo.image-source-evidence.v1";
export const GENERATED_UI_VERIFICATION_POLICY_SCHEMA_VERSION =
  "cogo.generated-ui-verification-policy.v1";
export const GENERATED_UI_VERIFICATION_SUMMARY_SCHEMA_VERSION =
  "cogo.generated-ui-verification-summary.v1";
export const IMAGE_EVIDENCE_VERIFICATION_POC_SCHEMA_VERSION =
  "cogo.image-evidence-verification-poc.v1";
export const IMAGE_EVIDENCE_CANDIDATE_ADMISSION_SCHEMA_VERSION =
  "cogo.image-evidence-candidate-admission.v1";

export const CANONICAL_GENERATED_UI_PROVENANCE_TIERS = [
  "ontology-direct-reuse",
  "ui-block-context-changed",
  "design-system-generated",
  "image-derived-candidate",
  "lower-layer-generated-candidate",
] as const;

export type ImageEvidenceSourceKind =
  | "url"
  | "pdf"
  | "image"
  | "app-screenshot"
  | "screen-recording"
  | "manual-crop";

export type ImageEvidenceSourceAuthority =
  | "structured-source-authority"
  | "visual-evidence-only"
  | "candidate-source-input";

export type CanonicalGeneratedUiProvenanceTier =
  (typeof CANONICAL_GENERATED_UI_PROVENANCE_TIERS)[number];

export type LegacyGeneratedUiProvenanceTier =
  | "ontology-exact-reuse"
  | "template-slot-reuse"
  | "template-modified";

export type GeneratedUiProvenanceTier =
  | CanonicalGeneratedUiProvenanceTier
  | LegacyGeneratedUiProvenanceTier;

export type GeneratedUiVerificationLevel =
  | "provenance-only"
  | "lightweight"
  | "medium"
  | "strict"
  | "strictest";

export type ImageEvidenceGateStatus = "pass" | "blocked" | "skipped";
export type ImageEvidenceExpectedOutcome = "pass" | "blocked";

export interface ImageEvidenceBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface ImageEvidenceTile {
  tileId: string;
  bbox: ImageEvidenceBox;
  candidateRole: string;
  confidence: number;
  visibleText?: string[];
}

export interface PixelRagVisualSegment {
  segmentId: string;
  roleHint: string;
  bbox: ImageEvidenceBox;
  confidence?: number;
  ocrText?: string[];
  sourcePage?: string;
}

export interface ImageSourceEvidence {
  schemaVersion: typeof IMAGE_SOURCE_EVIDENCE_SCHEMA_VERSION;
  sourceId: string;
  sourceKind: ImageEvidenceSourceKind;
  structuredSourceAvailable: boolean;
  authority: ImageEvidenceSourceAuthority;
  capture: {
    tool: "pixelshot-poc" | "manual-fixture";
    viewport: { width: number; height: number };
    deviceClass: "mobile" | "tablet" | "desktop";
    capturedAt: string;
    sourceDigest: string;
  };
  tiles: ImageEvidenceTile[];
  inferredCandidates: {
    pageRecipes: string[];
    uiBlocks: string[];
    components: string[];
  };
  unresolvedAmbiguity: string[];
  promotion: {
    status: "visual-evidence-only" | "candidate-review-required";
    reason: string;
  };
}

export interface GeneratedUiVerificationSurface {
  id: string;
  provenanceTier: GeneratedUiProvenanceTier;
  changedFields: string[];
  schemaValid: boolean;
  compilerNoChanges?: boolean;
  ontologyAsset?: {
    id: string;
    version: string;
    digestMatch: boolean;
  };
  sourceEvidence?: ImageSourceEvidence;
  usesRasterSourceAsOutput?: boolean;
  inferredHiddenTruths?: string[];
}

export interface GeneratedUiVerificationPolicy {
  schemaVersion: typeof GENERATED_UI_VERIFICATION_POLICY_SCHEMA_VERSION;
  surfaceId: string;
  provenanceTier: CanonicalGeneratedUiProvenanceTier;
  verificationLevel: GeneratedUiVerificationLevel;
  requiredGates: string[];
  skippedGates: Array<{ gate: string; reason: string }>;
  promotionAllowed: boolean;
}

export interface GeneratedUiVerificationSurfaceResult {
  surfaceId: string;
  provenanceTier: CanonicalGeneratedUiProvenanceTier;
  verificationLevel: GeneratedUiVerificationLevel;
  promotionAllowed: boolean;
  gates: ImageEvidenceGateResult[];
  issues: string[];
}

export interface GeneratedUiVerificationSummary {
  schemaVersion: typeof GENERATED_UI_VERIFICATION_SUMMARY_SCHEMA_VERSION;
  enabled: boolean;
  status: "pass" | "blocked" | "skipped";
  surfaceCount: number;
  promotionAllowed: boolean;
  canPublish: boolean;
  requiredEvidence: string[];
  blockedReasons: string[];
  surfaces: GeneratedUiVerificationSurfaceResult[];
}

export type ImageEvidenceCandidateAdmissionDecision =
  | "admit"
  | "reject"
  | "quarantine";

export type ImageEvidenceCandidateAdmissionStatus =
  | "review-required"
  | "admitted"
  | "rejected"
  | "quarantined"
  | "blocked";

export interface ImageEvidenceCandidateAdmissionReview {
  decision: ImageEvidenceCandidateAdmissionDecision;
  reviewedAt: string;
  reviewerKind: "operator" | "automated-oracle";
  evidenceArtifacts: string[];
  visualDiffScore?: number;
  generatedCogoDigest?: string;
  notes?: string[];
}

export interface ImageEvidenceCandidateAdmissionResult {
  schemaVersion: typeof IMAGE_EVIDENCE_CANDIDATE_ADMISSION_SCHEMA_VERSION;
  candidateId: string;
  sourceTier: "image-derived-candidate";
  status: ImageEvidenceCandidateAdmissionStatus;
  canSeedOntology: boolean;
  canPublishFromCurrentGeneration: false;
  requiredEvidence: string[];
  blockedReasons: string[];
  notes?: string[];
  review?: ImageEvidenceCandidateAdmissionReview;
}

export interface ImageEvidenceGateResult {
  gate: string;
  status: ImageEvidenceGateStatus;
  detail: string;
}

export interface ImageEvidenceScenario {
  id: string;
  description: string;
  surface: GeneratedUiVerificationSurface;
  expectedOutcome: ImageEvidenceExpectedOutcome;
}

export interface ImageEvidenceScenarioResult {
  id: string;
  description: string;
  expectedOutcome: ImageEvidenceExpectedOutcome;
  actualOutcome: "pass" | "blocked";
  expectationMet: boolean;
  policy: GeneratedUiVerificationPolicy;
  gates: ImageEvidenceGateResult[];
  issues: string[];
}

export interface ImageEvidenceVerificationPocReport {
  schemaVersion: typeof IMAGE_EVIDENCE_VERIFICATION_POC_SCHEMA_VERSION;
  createdAt: string;
  status: "pass" | "blocked";
  purpose: string[];
  evidence: {
    imageSourceEvidence: ImageSourceEvidence;
    scenarioCount: number;
    passedExpectationCount: number;
    failedExpectationCount: number;
  };
  results: ImageEvidenceScenarioResult[];
  outputPath?: string;
}

export interface EvaluateImageEvidenceCandidateAdmissionOptions {
  candidateId: string;
  sourceEvidence: ImageSourceEvidence;
  generatedUiVerification: GeneratedUiVerificationSummary;
  review?: ImageEvidenceCandidateAdmissionReview;
}

export const IMAGE_EVIDENCE_ROLE_TO_BLOCK: Record<string, string> = {
  "app-header-search": "app-header-search-candidate",
  "promo-hero-card": "promo-hero-card-candidate",
  "category-tab-strip": "category-tab-strip-candidate",
  "product-card-grid": "asymmetric-product-card-grid-candidate",
  "bottom-tab-nav": "bottom-tab-nav-candidate",
  "primary-cta": "primary-cta-candidate",
  "finance-balance-card": "finance-balance-card-candidate",
  "finance-quick-action-grid": "finance-quick-action-grid-candidate",
  "finance-transaction-list": "finance-transaction-list-candidate",
};

export const GENERATED_UI_POLICY_BY_TIER: Record<
  CanonicalGeneratedUiProvenanceTier,
  Omit<
    GeneratedUiVerificationPolicy,
    "schemaVersion" | "surfaceId" | "provenanceTier" | "promotionAllowed"
  >
> = {
  "ontology-direct-reuse": {
    verificationLevel: "provenance-only",
    requiredGates: [
      "schema-valid",
      "ontology-asset-digest-match",
      "provenance-trace",
    ],
    skippedGates: [
      {
        gate: "browser-visual-diff",
        reason: "unchanged admitted ontology asset with matching digest",
      },
      {
        gate: "full-design-system-quality-review",
        reason: "no generated layout, token, content, or context change",
      },
    ],
  },
  "ui-block-context-changed": {
    verificationLevel: "lightweight",
    requiredGates: [
      "schema-valid",
      "slot-content-visible",
      "slot-fit",
      "locked-feature-preserved",
      "non-edited-region-visual-diff",
      "no-raster-only-pass",
    ],
    skippedGates: [
      {
        gate: "full-design-system-quality-review",
        reason:
          "layout and UI block anatomy reused from admitted template ontology",
      },
    ],
  },
  "design-system-generated": {
    verificationLevel: "strict",
    requiredGates: [
      "schema-valid",
      "token-conformance",
      "component-anatomy",
      "state-recipe-conformance",
      "layout-quality",
      "responsive-constraints",
      "accessibility",
      "visible-from-json",
      "rendered-visibility",
      "visual-quality-review",
      "no-raster-only-pass",
    ],
    skippedGates: [],
  },
  "image-derived-candidate": {
    verificationLevel: "strictest",
    requiredGates: [
      "schema-valid",
      "source-provenance",
      "tile-coverage",
      "visual-structure-readback",
      "candidate-review",
      "ambiguity-recorded",
      "hidden-truth-not-inferred",
      "no-raster-only-pass",
    ],
    skippedGates: [],
  },
  "lower-layer-generated-candidate": {
    verificationLevel: "strict",
    requiredGates: [
      "schema-valid",
      "lower-layer-fallback-trace",
      "candidate-review",
      "component-anatomy",
      "rendered-visibility",
      "accessibility",
      "no-raster-only-pass",
    ],
    skippedGates: [],
  },
};

function unique(values: string[]): string[] {
  return [...new Set(values)].sort();
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === "object" && !Array.isArray(value));
}

function isNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function isStringArray(value: unknown): value is string[] {
  return (
    Array.isArray(value) && value.every((item) => typeof item === "string")
  );
}

export function normalizeGeneratedUiProvenanceTier(
  tier: GeneratedUiProvenanceTier,
): CanonicalGeneratedUiProvenanceTier {
  if (tier === "ontology-exact-reuse") return "ontology-direct-reuse";
  if (tier === "template-slot-reuse") return "ui-block-context-changed";
  if (tier === "template-modified") return "design-system-generated";
  return tier;
}

export function buildImageEvidenceTilesFromPixelRagSegments(
  segments: PixelRagVisualSegment[],
): ImageEvidenceTile[] {
  return segments.map((segment) => ({
    tileId: segment.segmentId,
    bbox: segment.bbox,
    candidateRole: segment.roleHint,
    confidence: segment.confidence ?? 0.75,
    ...(segment.ocrText ? { visibleText: segment.ocrText } : {}),
  }));
}

export function validateImageSourceEvidenceContract(value: unknown): {
  valid: boolean;
  issues: string[];
} {
  const issues: string[] = [];
  const record = isRecord(value) ? value : null;
  if (!record) {
    return { valid: false, issues: ["ImageSourceEvidence must be an object"] };
  }
  if (record.schemaVersion !== IMAGE_SOURCE_EVIDENCE_SCHEMA_VERSION) {
    issues.push(
      `ImageSourceEvidence.schemaVersion must be ${IMAGE_SOURCE_EVIDENCE_SCHEMA_VERSION}`,
    );
  }
  if (typeof record.sourceId !== "string" || record.sourceId.length === 0) {
    issues.push("ImageSourceEvidence.sourceId is required");
  }
  if (typeof record.structuredSourceAvailable !== "boolean") {
    issues.push(
      "ImageSourceEvidence.structuredSourceAvailable must be boolean",
    );
  }
  if (
    record.authority !== "structured-source-authority" &&
    record.authority !== "visual-evidence-only" &&
    record.authority !== "candidate-source-input"
  ) {
    issues.push("ImageSourceEvidence.authority is invalid");
  }
  const capture = isRecord(record.capture) ? record.capture : null;
  if (!capture) {
    issues.push("ImageSourceEvidence.capture is required");
  } else {
    const viewport = isRecord(capture.viewport) ? capture.viewport : null;
    if (!viewport || !isNumber(viewport.width) || !isNumber(viewport.height)) {
      issues.push(
        "ImageSourceEvidence.capture.viewport width/height are required",
      );
    }
    if (
      typeof capture.capturedAt !== "string" ||
      capture.capturedAt.length === 0
    ) {
      issues.push("ImageSourceEvidence.capture.capturedAt is required");
    }
    if (
      typeof capture.sourceDigest !== "string" ||
      capture.sourceDigest.length === 0
    ) {
      issues.push("ImageSourceEvidence.capture.sourceDigest is required");
    }
  }
  const tiles = Array.isArray(record.tiles) ? record.tiles : null;
  if (!tiles) {
    issues.push("ImageSourceEvidence.tiles must be an array");
  } else {
    for (const tile of tiles) {
      const tileRecord = isRecord(tile) ? tile : null;
      const bbox = isRecord(tileRecord?.bbox) ? tileRecord?.bbox : null;
      if (
        !tileRecord ||
        typeof tileRecord.tileId !== "string" ||
        typeof tileRecord.candidateRole !== "string" ||
        !isNumber(tileRecord.confidence) ||
        !bbox ||
        !isNumber(bbox.x) ||
        !isNumber(bbox.y) ||
        !isNumber(bbox.width) ||
        !isNumber(bbox.height)
      ) {
        issues.push("ImageSourceEvidence.tiles contains an invalid tile");
        break;
      }
    }
  }
  const promotion = isRecord(record.promotion) ? record.promotion : null;
  if (!promotion) {
    issues.push("ImageSourceEvidence.promotion is required");
  } else if (
    promotion.status !== "visual-evidence-only" &&
    promotion.status !== "candidate-review-required"
  ) {
    issues.push("ImageSourceEvidence.promotion.status is invalid");
  }
  if (
    record.structuredSourceAvailable === false &&
    promotion?.status !== "candidate-review-required"
  ) {
    issues.push(
      "image-derived source without structured source must remain candidate-review-required",
    );
  }
  return { valid: issues.length === 0, issues };
}

export function validateGeneratedUiVerificationSurfaceContract(
  value: unknown,
): { valid: boolean; issues: string[] } {
  const issues: string[] = [];
  const record = isRecord(value) ? value : null;
  if (!record) {
    return {
      valid: false,
      issues: ["GeneratedUiVerificationSurface must be an object"],
    };
  }
  if (typeof record.id !== "string" || record.id.length === 0) {
    issues.push("GeneratedUiVerificationSurface.id is required");
  }
  if (
    typeof record.provenanceTier !== "string" ||
    !CANONICAL_GENERATED_UI_PROVENANCE_TIERS.includes(
      normalizeGeneratedUiProvenanceTier(
        record.provenanceTier as GeneratedUiProvenanceTier,
      ),
    )
  ) {
    issues.push("GeneratedUiVerificationSurface.provenanceTier is invalid");
  }
  if (!Array.isArray(record.changedFields)) {
    issues.push(
      "GeneratedUiVerificationSurface.changedFields must be an array",
    );
  } else if (!isStringArray(record.changedFields)) {
    issues.push(
      "GeneratedUiVerificationSurface.changedFields must contain strings only",
    );
  }
  if (typeof record.schemaValid !== "boolean") {
    issues.push("GeneratedUiVerificationSurface.schemaValid must be boolean");
  }
  if (record.sourceEvidence !== undefined) {
    const sourceEvidence = validateImageSourceEvidenceContract(
      record.sourceEvidence,
    );
    if (!sourceEvidence.valid) {
      issues.push(
        ...sourceEvidence.issues.map((issue) => `sourceEvidence.${issue}`),
      );
    }
  }
  return { valid: issues.length === 0, issues };
}

export function validateGeneratedUiVerificationPolicyContract(value: unknown): {
  valid: boolean;
  issues: string[];
} {
  const issues: string[] = [];
  const record = isRecord(value) ? value : null;
  if (!record) {
    return {
      valid: false,
      issues: ["GeneratedUiVerificationPolicy must be an object"],
    };
  }
  if (
    record.schemaVersion !== GENERATED_UI_VERIFICATION_POLICY_SCHEMA_VERSION
  ) {
    issues.push(
      `GeneratedUiVerificationPolicy.schemaVersion must be ${GENERATED_UI_VERIFICATION_POLICY_SCHEMA_VERSION}`,
    );
  }
  if (typeof record.surfaceId !== "string" || record.surfaceId.length === 0) {
    issues.push("GeneratedUiVerificationPolicy.surfaceId is required");
  }
  if (
    typeof record.provenanceTier !== "string" ||
    !CANONICAL_GENERATED_UI_PROVENANCE_TIERS.includes(
      record.provenanceTier as CanonicalGeneratedUiProvenanceTier,
    )
  ) {
    issues.push(
      "GeneratedUiVerificationPolicy.provenanceTier must be canonical",
    );
  }
  if (typeof record.verificationLevel !== "string") {
    issues.push("GeneratedUiVerificationPolicy.verificationLevel is required");
  }
  if (!isStringArray(record.requiredGates)) {
    issues.push("GeneratedUiVerificationPolicy.requiredGates must be string[]");
  }
  if (!Array.isArray(record.skippedGates)) {
    issues.push("GeneratedUiVerificationPolicy.skippedGates must be an array");
  }
  if (typeof record.promotionAllowed !== "boolean") {
    issues.push(
      "GeneratedUiVerificationPolicy.promotionAllowed must be boolean",
    );
  }
  return { valid: issues.length === 0, issues };
}

export function inferImageSourceCandidatesFromTiles(
  tiles: ImageEvidenceTile[],
): ImageSourceEvidence["inferredCandidates"] {
  const roles = new Set(tiles.map((tile) => tile.candidateRole));
  const uiBlocks = unique(
    tiles
      .map((tile) => IMAGE_EVIDENCE_ROLE_TO_BLOCK[tile.candidateRole])
      .filter((value): value is string => Boolean(value)),
  );
  const components = unique(
    tiles
      .filter(
        (tile) =>
          tile.candidateRole.includes("cta") ||
          tile.candidateRole.includes("search"),
      )
      .map((tile) => `${tile.candidateRole}-component-candidate`),
  );
  const financeRoleCount = [
    "finance-balance-card",
    "finance-quick-action-grid",
    "finance-transaction-list",
  ].filter((role) => roles.has(role)).length;
  return {
    pageRecipes:
      financeRoleCount >= 3
        ? ["mobile-finance-dashboard-candidate"]
        : uiBlocks.length >= 4
          ? ["mobile-commerce-discover-candidate"]
          : [],
    uiBlocks,
    components,
  };
}

export function buildImageSourceEvidence(options: {
  sourceId: string;
  sourceKind: ImageEvidenceSourceKind;
  structuredSourceAvailable: boolean;
  tiles: ImageEvidenceTile[];
  sourceDigest: string;
  capturedAt: string;
  captureTool?: ImageSourceEvidence["capture"]["tool"];
  viewport?: { width: number; height: number };
  deviceClass?: ImageSourceEvidence["capture"]["deviceClass"];
  unresolvedAmbiguity?: string[];
}): ImageSourceEvidence {
  const authority: ImageEvidenceSourceAuthority =
    options.structuredSourceAvailable
      ? "visual-evidence-only"
      : "candidate-source-input";
  return {
    schemaVersion: IMAGE_SOURCE_EVIDENCE_SCHEMA_VERSION,
    sourceId: options.sourceId,
    sourceKind: options.sourceKind,
    structuredSourceAvailable: options.structuredSourceAvailable,
    authority,
    capture: {
      tool: options.captureTool ?? "pixelshot-poc",
      viewport: options.viewport ?? { width: 390, height: 844 },
      deviceClass: options.deviceClass ?? "mobile",
      capturedAt: options.capturedAt,
      sourceDigest: options.sourceDigest,
    },
    tiles: options.tiles,
    inferredCandidates: inferImageSourceCandidatesFromTiles(options.tiles),
    unresolvedAmbiguity: options.unresolvedAmbiguity ?? [],
    promotion: options.structuredSourceAvailable
      ? {
          status: "visual-evidence-only",
          reason:
            "structured source is available; screenshots are readback evidence only",
        }
      : {
          status: "candidate-review-required",
          reason:
            "image-derived source cannot enter reusable ontology without review",
        },
  };
}

export function buildGeneratedUiVerificationPolicy(
  surface: GeneratedUiVerificationSurface,
): GeneratedUiVerificationPolicy {
  const provenanceTier = normalizeGeneratedUiProvenanceTier(
    surface.provenanceTier,
  );
  const base =
    GENERATED_UI_POLICY_BY_TIER[provenanceTier] ??
    GENERATED_UI_POLICY_BY_TIER["design-system-generated"];
  const candidateTier =
    provenanceTier === "image-derived-candidate" ||
    provenanceTier === "lower-layer-generated-candidate";
  return {
    schemaVersion: GENERATED_UI_VERIFICATION_POLICY_SCHEMA_VERSION,
    surfaceId: surface.id,
    provenanceTier,
    verificationLevel: base.verificationLevel,
    requiredGates: base.requiredGates,
    skippedGates: base.skippedGates,
    promotionAllowed: !candidateTier,
  };
}

export function validateGeneratedUiVerificationSurface(
  surface: GeneratedUiVerificationSurface,
  policy = buildGeneratedUiVerificationPolicy(surface),
): { gates: ImageEvidenceGateResult[]; issues: string[] } {
  const provenanceTier = normalizeGeneratedUiProvenanceTier(
    surface.provenanceTier,
  );
  const changedFields = Array.isArray(surface.changedFields)
    ? surface.changedFields.filter(
        (field): field is string => typeof field === "string",
      )
    : [];
  const issues: string[] = [
    ...validateGeneratedUiVerificationSurfaceContract(surface).issues,
    ...validateGeneratedUiVerificationPolicyContract(policy).issues,
  ];

  if (!surface.schemaValid)
    issues.push("generated Cogo JSON must be schema-valid");

  if (surface.usesRasterSourceAsOutput) {
    issues.push(
      "source raster or screenshot tile cannot become a commercial-ready editable UI pass",
    );
  }

  if (provenanceTier === "ontology-direct-reuse") {
    if (changedFields.length > 0) {
      issues.push(
        "ontology-direct-reuse cannot have content, context, token, or layout changes",
      );
    }
    if (surface.compilerNoChanges !== true) {
      issues.push("ontology-direct-reuse requires compilerNoChanges=true");
    }
    if (!surface.ontologyAsset?.digestMatch) {
      issues.push("ontology-direct-reuse requires ontology asset digest match");
    }
  }

  if (provenanceTier === "ui-block-context-changed") {
    const invalidChanges = changedFields.filter(
      (field) => !field.startsWith("slots."),
    );
    if (invalidChanges.length > 0) {
      issues.push(
        `ui-block-context-changed may only change declared slots: ${invalidChanges.join(", ")}`,
      );
    }
  }

  if (provenanceTier === "image-derived-candidate") {
    if (!surface.sourceEvidence)
      issues.push("image-derived-candidate requires ImageSourceEvidence");
    if (surface.sourceEvidence?.structuredSourceAvailable) {
      issues.push(
        "image-derived-candidate is invalid when structured source is available",
      );
    }
    if (
      surface.sourceEvidence?.promotion.status !== "candidate-review-required"
    ) {
      issues.push(
        "image-derived-candidate must remain candidate-review-required",
      );
    }
    if ((surface.sourceEvidence?.tiles.length ?? 0) === 0) {
      issues.push("image-derived-candidate requires screenshot tiles");
    }
    if (
      (surface.sourceEvidence?.inferredCandidates.uiBlocks.length ?? 0) === 0
    ) {
      issues.push(
        "image-derived-candidate requires at least one inferred UI block candidate",
      );
    }
    if ((surface.inferredHiddenTruths ?? []).length > 0) {
      issues.push(
        `image evidence cannot infer hidden truths: ${(surface.inferredHiddenTruths ?? []).join(", ")}`,
      );
    }
  }

  if (
    provenanceTier === "lower-layer-generated-candidate" &&
    policy.promotionAllowed
  ) {
    issues.push(
      "lower-layer-generated-candidate cannot be promotionAllowed before review",
    );
  }

  const blockedGates = new Set<string>();
  if (issues.some((issue) => issue.includes("schema-valid")))
    blockedGates.add("schema-valid");
  if (issues.some((issue) => issue.includes("raster")))
    blockedGates.add("no-raster-only-pass");
  if (issues.some((issue) => issue.includes("hidden truths"))) {
    blockedGates.add("hidden-truth-not-inferred");
  }
  if (issues.some((issue) => issue.includes("candidate-review-required"))) {
    blockedGates.add("candidate-review");
  }
  if (
    !policy.promotionAllowed &&
    policy.requiredGates.includes("candidate-review")
  ) {
    blockedGates.add("candidate-review");
  }
  if (issues.some((issue) => issue.includes("digest"))) {
    blockedGates.add("ontology-asset-digest-match");
  }

  const gates: ImageEvidenceGateResult[] = policy.requiredGates.map((gate) => ({
    gate,
    status: blockedGates.has(gate) ? ("blocked" as const) : ("pass" as const),
    detail:
      blockedGates.has(gate) && gate === "candidate-review"
        ? "requires review/admission before publish promotion"
        : blockedGates.has(gate)
          ? "failed by policy validation"
          : "satisfied by policy validation",
  }));
  gates.push(
    ...policy.skippedGates.map((gate) => ({
      gate: gate.gate,
      status: "skipped" as const,
      detail: gate.reason,
    })),
  );
  return { gates, issues };
}

export function buildGeneratedUiVerificationSummary(options: {
  enabled: boolean;
  surfaces: GeneratedUiVerificationSurface[];
}): GeneratedUiVerificationSummary {
  if (!options.enabled) {
    return {
      schemaVersion: GENERATED_UI_VERIFICATION_SUMMARY_SCHEMA_VERSION,
      enabled: false,
      status: "skipped",
      surfaceCount: 0,
      promotionAllowed: true,
      canPublish: true,
      requiredEvidence: [],
      blockedReasons: [],
      surfaces: [],
    };
  }

  if (options.surfaces.length === 0) {
    return {
      schemaVersion: GENERATED_UI_VERIFICATION_SUMMARY_SCHEMA_VERSION,
      enabled: true,
      status: "blocked",
      surfaceCount: 0,
      promotionAllowed: false,
      canPublish: false,
      requiredEvidence: ["generated-ui-verification-surface"],
      blockedReasons: [
        "generated UI verification is enabled but no verification surface was attached",
      ],
      surfaces: [],
    };
  }

  const surfaces = options.surfaces.map((surface) => {
    const policy = buildGeneratedUiVerificationPolicy(surface);
    const result = validateGeneratedUiVerificationSurface(surface, policy);
    return {
      surfaceId: surface.id,
      provenanceTier: policy.provenanceTier,
      verificationLevel: policy.verificationLevel,
      promotionAllowed: policy.promotionAllowed,
      gates: result.gates,
      issues: result.issues,
    };
  });
  const blockedReasons = surfaces.flatMap((surface) =>
    surface.issues.map((issue) => `${surface.surfaceId}: ${issue}`),
  );
  const requiredEvidence = unique(
    surfaces.flatMap((surface) =>
      surface.gates
        .filter((gate) => gate.status !== "skipped")
        .map((gate) => gate.gate),
    ),
  );
  const promotionAllowed =
    surfaces.length > 0 &&
    surfaces.every(
      (surface) => surface.promotionAllowed && surface.issues.length === 0,
    );
  return {
    schemaVersion: GENERATED_UI_VERIFICATION_SUMMARY_SCHEMA_VERSION,
    enabled: true,
    status: blockedReasons.length === 0 ? "pass" : "blocked",
    surfaceCount: surfaces.length,
    promotionAllowed,
    canPublish: blockedReasons.length === 0 && promotionAllowed,
    requiredEvidence,
    blockedReasons,
    surfaces,
  };
}

const IMAGE_EVIDENCE_ADMISSION_REQUIRED_ARTIFACTS = [
  "source-image",
  "generated-cogo-render",
  "visual-diff",
  "cogo-json-manifest",
] as const;

export function evaluateImageEvidenceCandidateAdmission(
  options: EvaluateImageEvidenceCandidateAdmissionOptions,
): ImageEvidenceCandidateAdmissionResult {
  const requiredEvidence = [
    "image-source-evidence",
    "generated-ui-verification-summary",
    "operator-or-oracle-review",
    ...IMAGE_EVIDENCE_ADMISSION_REQUIRED_ARTIFACTS,
  ];
  const blockedReasons: string[] = [];
  const sourceContract = validateImageSourceEvidenceContract(
    options.sourceEvidence,
  );
  if (!sourceContract.valid) {
    blockedReasons.push(...sourceContract.issues);
  }
  if (options.sourceEvidence.structuredSourceAvailable) {
    blockedReasons.push(
      "candidate admission is only for source-unavailable image-derived candidates",
    );
  }
  if (options.generatedUiVerification.status !== "pass") {
    blockedReasons.push(
      "generated UI verification must pass before candidate admission review",
    );
  }
  if (options.generatedUiVerification.promotionAllowed) {
    blockedReasons.push(
      "candidate admission expects a candidate-only verification summary",
    );
  }

  if (!options.review) {
    return {
      schemaVersion: IMAGE_EVIDENCE_CANDIDATE_ADMISSION_SCHEMA_VERSION,
      candidateId: options.candidateId,
      sourceTier: "image-derived-candidate",
      status: blockedReasons.length > 0 ? "blocked" : "review-required",
      canSeedOntology: false,
      canPublishFromCurrentGeneration: false,
      requiredEvidence,
      blockedReasons,
    };
  }

  const missingArtifacts = IMAGE_EVIDENCE_ADMISSION_REQUIRED_ARTIFACTS.filter(
    (artifact) => !options.review?.evidenceArtifacts.includes(artifact),
  );
  if (missingArtifacts.length > 0) {
    blockedReasons.push(
      `candidate admission review is missing artifacts: ${missingArtifacts.join(", ")}`,
    );
  }
  if (
    typeof options.review.visualDiffScore === "number" &&
    (options.review.visualDiffScore < 0 || options.review.visualDiffScore > 1)
  ) {
    blockedReasons.push("candidate admission visualDiffScore must be 0..1");
  }
  if (!options.review.generatedCogoDigest) {
    blockedReasons.push(
      "candidate admission review requires generatedCogoDigest",
    );
  }

  if (blockedReasons.length > 0) {
    return {
      schemaVersion: IMAGE_EVIDENCE_CANDIDATE_ADMISSION_SCHEMA_VERSION,
      candidateId: options.candidateId,
      sourceTier: "image-derived-candidate",
      status: "blocked",
      canSeedOntology: false,
      canPublishFromCurrentGeneration: false,
      requiredEvidence,
      blockedReasons,
      review: options.review,
    };
  }

  if (options.review.decision === "reject") {
    return {
      schemaVersion: IMAGE_EVIDENCE_CANDIDATE_ADMISSION_SCHEMA_VERSION,
      candidateId: options.candidateId,
      sourceTier: "image-derived-candidate",
      status: "rejected",
      canSeedOntology: false,
      canPublishFromCurrentGeneration: false,
      requiredEvidence,
      blockedReasons: ["candidate review rejected ontology admission"],
      review: options.review,
    };
  }
  if (options.review.decision === "quarantine") {
    return {
      schemaVersion: IMAGE_EVIDENCE_CANDIDATE_ADMISSION_SCHEMA_VERSION,
      candidateId: options.candidateId,
      sourceTier: "image-derived-candidate",
      status: "quarantined",
      canSeedOntology: false,
      canPublishFromCurrentGeneration: false,
      requiredEvidence,
      blockedReasons: ["candidate review quarantined ontology admission"],
      review: options.review,
    };
  }

  return {
    schemaVersion: IMAGE_EVIDENCE_CANDIDATE_ADMISSION_SCHEMA_VERSION,
    candidateId: options.candidateId,
    sourceTier: "image-derived-candidate",
    status: "admitted",
    canSeedOntology: true,
    canPublishFromCurrentGeneration: false,
    requiredEvidence,
    blockedReasons: [],
    notes: [
      "current generation remains candidate-sourced; publish must reuse the admitted ontology asset in a later generation",
    ],
    review: options.review,
  };
}

export function evaluateImageEvidenceScenario(
  scenario: ImageEvidenceScenario,
): ImageEvidenceScenarioResult {
  const policy = buildGeneratedUiVerificationPolicy(scenario.surface);
  const { gates, issues } = validateGeneratedUiVerificationSurface(
    scenario.surface,
    policy,
  );
  const actualOutcome = issues.length === 0 ? "pass" : "blocked";
  return {
    id: scenario.id,
    description: scenario.description,
    expectedOutcome: scenario.expectedOutcome,
    actualOutcome,
    expectationMet: actualOutcome === scenario.expectedOutcome,
    policy,
    gates,
    issues,
  };
}

export function buildImageEvidenceVerificationPocReport(options: {
  createdAt: string;
  imageSourceEvidence: ImageSourceEvidence;
  scenarios: ImageEvidenceScenario[];
  outputPath?: string;
}): ImageEvidenceVerificationPocReport {
  const results = options.scenarios.map(evaluateImageEvidenceScenario);
  const failedExpectations = results.filter((result) => !result.expectationMet);
  return {
    schemaVersion: IMAGE_EVIDENCE_VERIFICATION_POC_SCHEMA_VERSION,
    createdAt: options.createdAt,
    status: failedExpectations.length === 0 ? "pass" : "blocked",
    purpose: [
      "prove image-derived source acquisition remains candidate-only",
      "prove generated UI verification intensity follows provenance tier",
      "prove unchanged ontology reuse avoids redundant expensive visual gates",
      "prove raster-backed output and hidden-truth inference are blocked",
    ],
    evidence: {
      imageSourceEvidence: options.imageSourceEvidence,
      scenarioCount: options.scenarios.length,
      passedExpectationCount: results.length - failedExpectations.length,
      failedExpectationCount: failedExpectations.length,
    },
    results,
    ...(options.outputPath ? { outputPath: options.outputPath } : {}),
  };
}
